import { http } from './api.service'
import store from '../store'

const application = process.env.VUE_APP_APPLICATION

/**
 * Thin wrapper around the Laravel AI agent endpoints (/v2/ai/*).
 *
 * The shared http instance already injects the `Domain` header (the currently
 * selected domain) which the API uses for role/team context. The `domainUuid`
 * arguments below are separate: they target which tenant a document belongs to
 * or a question is scoped against, and are sent in the body/query explicitly.
 */
const aiService = {
  // --- Quick agent ------------------------------------------------------
  ask ({ question, domainUuid = null, conversationId = null }) {
    return http.post('/ai/chat', {
      question,
      domain_uuid: domainUuid || null,
      conversation_id: conversationId || undefined
    }).then(({ data }) => data)
  },

  async stream ({ question, domainUuid = null, conversationId = null, onEvent, signal }) {
    const baseUrl = String(process.env.VUE_APP_API_URL || '').replace(/\/$/, '')
    const response = await fetch(`${baseUrl}/ai/chat/stream`, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${store.getters.jwt}`,
        Application: application,
        Domain: store.getters.domain.uuid,
        'X-DOMAIN': store.getters.domain.uuid,
        'X-APPLICATION': application,
        'X-SOURCE': 'administration'
      },
      body: JSON.stringify({
        question,
        domain_uuid: domainUuid || null,
        conversation_id: conversationId || undefined
      }),
      signal
    })

    if (!response.ok) {
      let data = {}
      try {
        data = await response.json()
      } catch (_) {}
      const error = new Error(data.message || `HTTP ${response.status}`)
      error.response = { status: response.status, data }
      throw error
    }

    if (!response.body) {
      parseSseBuffer(await response.text(), onEvent, true)
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
      buffer = parseSseBuffer(buffer, onEvent, done)
      if (done) {
        break
      }
    }
  },

  // --- Knowledge base ---------------------------------------------------
  listDocuments (domainUuid = null) {
    const params = domainUuid ? { domain_uuid: domainUuid } : {}
    return http.get('/ai/knowledge/documents', { params }).then(({ data }) => data)
  },

  ingestText (payload) {
    return http.post('/ai/knowledge/text', payload).then(({ data }) => data)
  },

  ingestFile (formData) {
    return http.post('/ai/knowledge/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => data)
  },

  ingestPdf (formData) {
    return this.ingestFile(formData)
  },

  deleteDocument (id, expectedRevision) {
    return http.delete(`/ai/knowledge/documents/${id}`, {
      data: { expected_revision: expectedRevision }
    }).then(({ data }) => data)
  },

  updateDocument (id, payload, expectedRevision) {
    return http.put(`/ai/knowledge/documents/${id}`, {
      ...payload,
      expected_revision: expectedRevision
    }).then(({ data }) => data)
  },

  getDocumentText (id) {
    return http.get(`/ai/knowledge/documents/${id}/text`).then(({ data }) => data)
  },

  updateDocumentText (id, text, expectedRevision, pageOffsets = []) {
    return http.put(`/ai/knowledge/documents/${id}/text`, {
      text,
      expected_revision: expectedRevision,
      page_offsets: pageOffsets
    }).then(({ data }) => data)
  },

  reindexDocument (id, expectedRevision) {
    return http.post(`/ai/knowledge/documents/${id}/reindex`, {
      expected_revision: expectedRevision
    }).then(({ data }) => data)
  },

  reextractDocument (id, expectedRevision) {
    return http.post(`/ai/knowledge/documents/${id}/reextract`, {
      replace_canonical: true,
      expected_revision: expectedRevision
    }).then(({ data }) => data)
  },

  downloadOriginalDocument (id) {
    return http.get(`/ai/knowledge/documents/${id}/original`, { responseType: 'blob' })
  },

  // --- Quotas (super-admin) --------------------------------------------
  listQuotas () {
    return http.get('/ai/quotas').then(({ data }) => data)
  },

  setDomainQuota (domainUuid, { domainMonthlyLimit = null, monthlyUserLimit }) {
    return http.put(`/ai/quotas/${domainUuid}`, {
      domain_monthly_limit: domainMonthlyLimit,
      monthly_user_limit: monthlyUserLimit
    }).then(({ data }) => data)
  },

  clearDomainQuota (domainUuid) {
    return http.delete(`/ai/quotas/${domainUuid}`).then(({ data }) => data)
  },

  // --- Tracking --------------------------------------------------------
  listTracking (domainUuid = null) {
    const params = domainUuid ? { domain_uuid: domainUuid } : {}
    return http.get('/ai/tracking', { params }).then(({ data }) => data)
  },

  exportTrackingCsv (domainUuid = null) {
    const params = domainUuid ? { domain_uuid: domainUuid } : {}
    return http.get('/ai/tracking/export', {
      params,
      responseType: 'blob'
    })
  }
}

function parseSseBuffer (buffer, onEvent, flush) {
  const normalized = buffer.replace(/\r\n/g, '\n')
  const blocks = normalized.split('\n\n')
  const remainder = flush ? '' : (blocks.pop() || '')

  blocks.forEach(block => emitSseBlock(block, onEvent))
  if (flush && blocks.length === 0 && normalized.trim()) {
    emitSseBlock(normalized, onEvent)
  }

  return remainder
}

function emitSseBlock (block, onEvent) {
  if (!block.trim()) {
    return
  }

  const lines = block.split('\n')
  const eventLine = lines.find(line => line.startsWith('event:'))
  const data = lines
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice(5).replace(/^\s+/, ''))
    .join('\n')

  if (!eventLine || !data) {
    return
  }

  try {
    onEvent({
      event: eventLine.slice(6).trim(),
      data: JSON.parse(data)
    })
  } catch (_) {}
}

export default aiService
export { aiService }
